import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as pdfjsLib from 'pdfjs-dist';
import pdfWorker from 'pdfjs-dist/build/pdf.worker.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

export type ExportAttachment = {
    id: string;
    expenseId: string;
    expenseTitle: string;
    fileName: string;
    url: string;
    type: 'image' | 'pdf';
};

export type ExportExpense = {
    id: string;
    date: string;
    type: string;
    typeName: string;
    typeColor?: string;
    typeIcon?: string;
    title: string;
    amount: number;
    description?: string;
    fuelLiters?: number;
    electricityKwh?: number;
    imageCount?: number;
    parts?: any[];
};

export type PdfExportOptions = {
    carName: string;
    carImageUrl?: string;
    carMake?: string;
    carModel?: string;
    carYear?: number;
    carPlate?: string;
    carVin?: string;
    carFuelType?: string;
    expenses: ExportExpense[];
    attachments: ExportAttachment[];
    currency: string;
    currencySymbol: string;
    language: string;
    dateFrom?: string;
    dateTo?: string;
    fullPeriod?: string;
    totalAmount: number;
    avgMonthly: number;
    cpk: number;
    distanceUnit: string;
    // translated labels
    labels: {
        date: string;
        type: string;
        title: string;
        amount: string;
        summary: string;
        totalCost: string;
        avgMonth: string;
        byType: string;
        count: string;
        ofTotal: string;
        generated: string;
        period: string;
        expensesSection?: string;
        liter?: string;
        gallon?: string;
        avgCost?: string;
        perUnit?: string;
        pageLabel?: string;
    };
};

async function fetchAsBase64(url: string): Promise<string> {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

/**
 * Robust image loader that tries crossOrigin=anonymous first, 
 * and falls back to a non-crossOrigin load if that fails (though non-crossOrigin may taint canvas).
 */
async function getRoundedImage(url: string, targetWidth: number, targetHeight: number, radius: number): Promise<string> {
    const timestamp = Date.now();
    const finalUrl = url.includes('?') ? `${url}&t=${timestamp}` : `${url}?t=${timestamp}`;

    let img: HTMLImageElement;
    try {
        const resp = await fetch(finalUrl, { mode: 'cors' });
        if (!resp.ok) throw new Error('Image fetch failed');
        const blob = await resp.blob();
        const blobUrl = URL.createObjectURL(blob);

        img = new Image();
        await new Promise((res, rej) => {
            const timeout = setTimeout(() => rej(new Error('Image render timeout')), 10000);
            img.onload = () => { clearTimeout(timeout); res(true); };
            img.onerror = () => { clearTimeout(timeout); rej(new Error('Image render failed')); };
            img.src = blobUrl;
        });
        URL.revokeObjectURL(blobUrl);
    } catch (e) {
        console.warn('Pdf image CORS or fetch failure', url, e);
        throw new Error('Image load failed');
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No canvas context');

    // Increase scale significantly for print quality (targets high DPI)
    const scale = 12;
    canvas.width = targetWidth * scale;
    canvas.height = targetHeight * scale;
    const r = radius * scale;

    // Create high-quality clipping path for rounded corners
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.lineTo(canvas.width - r, 0);
    ctx.quadraticCurveTo(canvas.width, 0, canvas.width, r);
    ctx.lineTo(canvas.width, canvas.height - r);
    ctx.quadraticCurveTo(canvas.width, canvas.height, canvas.width - r, canvas.height);
    ctx.lineTo(r, canvas.height);
    ctx.quadraticCurveTo(0, canvas.height, 0, canvas.height - r);
    ctx.lineTo(0, r);
    ctx.quadraticCurveTo(0, 0, r, 0);
    ctx.closePath();
    ctx.clip();

    // High quality image drawing
    const imgAspect = img.naturalWidth / img.naturalHeight;
    const targetAspect = targetWidth / targetHeight;
    let drawW, drawH, drawX, drawY;

    if (imgAspect > targetAspect) {
        drawH = canvas.height;
        drawW = canvas.height * imgAspect;
        drawX = (canvas.width - drawW) / 2;
        drawY = 0;
    } else {
        drawW = canvas.width;
        drawH = canvas.width / imgAspect;
        drawX = 0;
        drawY = (canvas.height - drawH) / 2;
    }

    // Use high quality scaling
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, drawX, drawY, drawW, drawH);

    // Use PNG for lossless transfer of the rounded result to jsPDF
    return canvas.toDataURL('image/png');
}

async function loadFonts(doc: jsPDF) {
    try {
        const urls = [
            'https://cdn.jsdelivr.net/npm/inter-font@3.19.0/ttf/Inter-Regular.ttf',
            'https://cdn.jsdelivr.net/npm/inter-font@3.19.0/ttf/Inter-Bold.ttf'
        ];
        const resps = await Promise.all(urls.map(u => fetch(u, { cache: 'force-cache' })));
        if (resps.some(r => !r.ok)) return false;
        const buffs = await Promise.all(resps.map(r => r.arrayBuffer()));
        const b64s = buffs.map(b => btoa(new Uint8Array(b).reduce((d, byte) => d + String.fromCharCode(byte), '')));
        doc.addFileToVFS('Inter-Regular.ttf', b64s[0]);
        doc.addFont('Inter-Regular.ttf', 'Inter', 'normal');
        doc.addFileToVFS('Inter-Bold.ttf', b64s[1]);
        doc.addFont('Inter-Bold.ttf', 'Inter', 'bold');
        return true;
    } catch { return false; }
}

function formatCurrencyPdf(amount: number, symbol: string, currency: string): string {
    const formatted = amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return ['USD', 'CAD', 'AUD', 'JPY', 'GBP'].includes(currency) ? `${symbol}${formatted}` : `${formatted} ${symbol}`;
}

function formatDate(dateStr: string): string {
    try {
        const d = new Date(dateStr);
        return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    } catch { return dateStr; }
}

export async function generateExpensePDF(options: PdfExportOptions): Promise<void> {
    const {
        carImageUrl, carMake, carModel, carYear, carPlate, carVin, carFuelType,
        expenses, attachments, currency, currencySymbol,
        dateFrom, dateTo, fullPeriod, totalAmount, avgMonthly, cpk, distanceUnit, labels,
    } = options;

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const hasFonts = await loadFonts(doc);
    const fontName = hasFonts ? 'Inter' : 'helvetica';
    const pageW = doc.internal.pageSize.getWidth(), pageH = doc.internal.pageSize.getHeight(), margin = 14;
    const contentW = pageW - 2 * margin, fmt = (v: number) => formatCurrencyPdf(v, currencySymbol, currency);

    // ─── HEADER ───────────────────────────────────────────────────────────────
    const headerH = 42;
    doc.setFillColor(59, 130, 246); doc.rect(0, 0, pageW, headerH, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont(fontName, 'bold'); doc.setFontSize(18);
    doc.text(`${carMake || ''} ${carModel || ''}`.trim() || '-', margin, 14);
    doc.setFont(fontName, 'normal'); doc.setFontSize(10.5);
    doc.text(`${carYear || ''} - ${carFuelType || ''}`.trim() || '-', margin, 21);
    doc.text(`${carPlate || ''} - ${carVin || ''}`.trim() || '-', margin, 27);
    doc.setFontSize(7.5); doc.setTextColor(219, 234, 254);
    const now = new Date();
    let metaStr = `${labels.generated}: ${now.getFullYear()}.${String(now.getMonth() + 1).padStart(2, '0')}.${String(now.getDate()).padStart(2, '0')}`;
    if (dateFrom || dateTo) metaStr += `  •  ${labels.period}: ${dateFrom ? formatDate(dateFrom) : '...'} – ${dateTo ? formatDate(dateTo) : '...'}`;
    doc.text(metaStr, margin, 35);

    if (carImageUrl) {
        try {
            const imW = 48, imH = 30;
            const b64 = await getRoundedImage(carImageUrl, imW, imH, 4);
            doc.addImage(b64, 'PNG', pageW - margin - imW, 6, imW, imH, undefined, 'SLOW');
        } catch (e) {
            console.error('Pdf image load error', e);
        }
    }

    // ─── SUMMARY ──────────────────────────────────────────────────────────────
    let y = headerH + 10;
    doc.setTextColor(30, 30, 30); doc.setFontSize(14); doc.setFont(fontName, 'bold');
    doc.text(labels.summary, margin, y);
    if (fullPeriod) {
        doc.setFontSize(8.5); doc.setFont(fontName, 'normal'); doc.setTextColor(107, 114, 128);
        doc.text(`${labels.period}: ${fullPeriod}`, margin, y + 5);
    }
    y += 10;
    const cardW = 45, cardG = 4, cards = [{ label: labels.totalCost, value: fmt(totalAmount) }, { label: labels.avgMonth, value: fmt(avgMonthly) }];
    if (cpk > 0) cards.push({ label: `${labels.avgCost || 'Cost'} ${labels.perUnit || '/'} ${distanceUnit}`, value: fmt(cpk) });
    cards.forEach((c, i) => {
        const cx = margin + i * (cardW + cardG);
        doc.setFillColor(248, 250, 252); doc.roundedRect(cx, y, cardW, 20, 3, 3, 'F');
        doc.setFontSize(8); doc.setFont(fontName, 'normal'); doc.setTextColor(100, 116, 139);
        doc.text(c.label.toUpperCase(), cx + 5, y + 7);
        doc.setFontSize(13); doc.setFont(fontName, 'bold'); doc.setTextColor(15, 23, 42);
        doc.text(c.value, cx + 5, y + 15);
    });
    y += 30;

    // ─── EXPENSE TABLE ────────────────────────────────────────────────────────
    doc.setTextColor(30, 30, 30); doc.setFontSize(14); doc.setFont(fontName, 'bold');
    doc.text(`${labels.expensesSection || labels.title} (${expenses.length})`, margin, y);
    y += 5;
    const tBody: any[] = [];
    let curY = '';
    [...expenses].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).forEach(e => {
        const yr = new Date(e.date).getFullYear().toString();
        if (yr !== curY) {
            const row = [yr, '', '', '']; (row as any).isY = true; tBody.push(row); curY = yr;
        }
        let lt = e.title;
        if ((e.type === 'fuel' || e.type === 'electricity') && (e.fuelLiters || e.electricityKwh) && e.amount) {
            const q = e.fuelLiters || e.electricityKwh || 0, u = e.type === 'fuel' ? (labels.liter || 'l') : 'kWh';
            lt = `${e.title || e.typeName} - ${q.toFixed(2)} ${u} / ${(e.amount / q).toFixed(3)} ${currencySymbol}`;
        }
        const showAmount = e.type !== 'info';
        const rData = [formatDate(e.date), e.typeName, lt, showAmount ? fmt(e.amount) : '']; (rData as any).exp = e; tBody.push(rData);
    });

    autoTable(doc, {
        startY: y, styles: { font: fontName, fontSize: 8.5, valign: 'middle' },
        head: [[labels.date, labels.type, labels.title, labels.amount]],
        body: tBody, margin: { left: margin, right: margin },
        headStyles: { fillColor: [59, 130, 246], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { textColor: [15, 23, 42], cellPadding: 3 },
        alternateRowStyles: { fillColor: [240, 243, 248] },
        columnStyles: { 0: { cellWidth: 25 }, 1: { cellWidth: 35 }, 3: { cellWidth: 35, halign: 'right' } },
        didParseCell: (d) => {
            const raw = d.row.raw as any;
            if (raw.isY) {
                d.cell.styles.fillColor = [255, 255, 255];
                if (d.column.index === 0) {
                    d.cell.styles.fontStyle = 'bold'; d.cell.styles.fontSize = 10; d.cell.styles.textColor = [59, 130, 246];
                }
                return;
            }
            if (d.section === 'head' && d.column.index === 3) d.cell.styles.halign = 'right';
            if (d.section === 'body' && d.column.index === 1) {
                const c = raw.exp?.typeColor;
                if (c) {
                    const h = c.replace('#', '');
                    d.cell.styles.textColor = [parseInt(h.substring(0, 2), 16), parseInt(h.substring(2, 4), 16), parseInt(h.substring(4, 6), 16)];
                    d.cell.styles.fontStyle = 'bold';
                }
            }
        }
    });

    // ─── ATTACHMENTS ──────────────────────────────────────────────────────────
    for (const at of attachments.filter(a => a.type === 'image')) {
        doc.addPage(); doc.setFillColor(59, 130, 246); doc.rect(0, 0, pageW, 18, 'F');
        doc.setFont(fontName, 'bold'); doc.setFontSize(11); doc.setTextColor(255, 255, 255); doc.text(at.expenseTitle, margin, 11);
        try {
            const b64 = await fetchAsBase64(at.url);
            const im = new Image(); await new Promise((r, j) => { im.onload = r; im.onerror = j; im.src = b64; });
            const s = Math.min(contentW / im.naturalWidth, (pageH - 40) / im.naturalHeight);
            doc.addImage(b64, 'JPEG', margin + (contentW - im.naturalWidth * s) / 2, 22, im.naturalWidth * s, im.naturalHeight * s, undefined, 'SLOW');
        } catch { }
    }
    for (const at of attachments.filter(a => a.type === 'pdf')) {
        try {
            const pdf = await pdfjsLib.getDocument({ url: at.url, withCredentials: false }).promise;
            for (let p = 1; p <= pdf.numPages; p++) {
                doc.addPage(); doc.setFillColor(71, 85, 105); doc.rect(0, 0, pageW, 18, 'F');
                doc.setFont(fontName, 'bold'); doc.setFontSize(10); doc.setTextColor(255, 255, 255); doc.text(`${at.expenseTitle} - ${labels.pageLabel || 'Page'} ${p}/${pdf.numPages}`, margin, 11);
                const pg = await pdf.getPage(p), vp = pg.getViewport({ scale: 2.0 }), canv = document.createElement('canvas');
                canv.width = vp.width; canv.height = vp.height; await pg.render({ canvas: canv, viewport: vp }).promise;
                const s = Math.min(contentW / vp.width, (pageH - 40) / vp.height);
                doc.addImage(canv.toDataURL('image/jpeg', 0.8), 'JPEG', margin + (contentW - vp.width * s) / 2, 22, vp.width * s, vp.height * s, undefined, 'SLOW');
            }
        } catch { }
    }

    const tP = doc.getNumberOfPages();
    for (let i = 1; i <= tP; i++) {
        doc.setPage(i); doc.setFont(fontName, 'normal'); doc.setFontSize(8); doc.setTextColor(150, 150, 150);
        doc.text(`${i} / ${tP}`, pageW / 2, pageH - 8, { align: 'center' });
    }
    window.open(URL.createObjectURL(doc.output('blob')), '_blank');
}
